import * as Sentry from '@sentry/react';

import { isPlainObject } from 'utils/parseUtils';
import { persistBag } from 'utils/user-merchant-metadata/persistUserMerchantMetadata';

import type { UserMerchantPreferencesBag, UserMerchantPreferencesScope } from 'model/types/user-merchant-preferences';

// The legacy medium and its namespace are hardcoded here, outside the generic namespace contract; a
// follow-up ticket drops both once the migration window closes.
const LEGACY_STORAGE_BASE_KEY = 'dataAnalyticsPreferences';
const MIGRATION_NAMESPACE = 'dataAnalytics';

function legacyStorageKey(scopeId: string): string {
  return `${LEGACY_STORAGE_BASE_KEY}:${scopeId}`;
}

function readLegacyEntry(storageKey: string): UserMerchantPreferencesBag | null {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return null;
    }

    const parsed: unknown = JSON.parse(stored);
    return isPlainObject(parsed) ? parsed : null;
  } catch {
    // a malformed or unreadable legacy entry is left alone and never migrated
    return null;
  }
}

function removeLegacyEntry(storageKey: string): void {
  try {
    localStorage.removeItem(storageKey);
  } catch (error) {
    Sentry.captureException(error);
  }
}

export function migrateLegacyPreferences(scope: UserMerchantPreferencesScope): UserMerchantPreferencesScope {
  if (MIGRATION_NAMESPACE in scope.bag) {
    return scope;
  }

  const storageKey = legacyStorageKey(scope.scopeId);
  const legacyValue = readLegacyEntry(storageKey);
  if (!legacyValue) {
    return scope;
  }

  // Sent immediately rather than debounced: the window in which the user can close the tab is the
  // one the migration has to survive. The legacy entry is dropped only once the write lands, so a
  // failure is retried on a later load instead of destroying the user's setup.
  persistBag({ roleId: scope.roleId, payload: { [MIGRATION_NAMESPACE]: legacyValue } })
    .then((persisted) => {
      if (persisted) {
        removeLegacyEntry(storageKey);
      }
    })
    .catch(Sentry.captureException);

  return { ...scope, bag: { ...scope.bag, [MIGRATION_NAMESPACE]: legacyValue } };
}
